import { isValidGtin, normalizeScan } from '@wms/domain';
import { useEffect, useRef } from 'react';

/*
 * Telling a barcode reader from a person
 * ======================================
 * The warehouse staff must read codes with a barcode reader or the camera; a typed code is refused
 * (supervisors may type). A reader, USB or Bluetooth, is a keyboard ("keyboard wedge"): it types the
 * code into the focused field and usually ends it with Enter. What gives it away is the pace:
 *
 *  - a USB reader sends a key every few milliseconds;
 *  - a Bluetooth reader sends the keys in bursts: back to back inside a burst, with pauses of
 *    100–400 ms (rarely more) between the radio's reports;
 *  - a person leaves 80 ms or more between nearly every two keys, on a PC keyboard or on the
 *    phone's screen, and some more before Enter.
 *
 * The rule (judgeScan), in order:
 *  1. shorter than 3 characters: typed;
 *  2. pasted, dropped or picked from the keyboard's suggestions, anything deleted or corrected, or
 *     a key held down (auto-repeat): typed;
 *  3. the whole code in one change (a reader, or its phone app, that hands it over at once): read,
 *     unless the change is an on-screen keyboard's composition (a word, a voice dictation);
 *  4. otherwise more than half of the gaps between the characters must be at most FAST_GAP_MS (the
 *     bursts), counting the Enter too when it follows the last character that fast (a reader's
 *     suffix; a later Enter counts neither way), and no pause between two characters may be longer
 *     than MAX_PAUSE_MS: read; else typed.
 *
 * The times come from the field's key presses when every character has one, and one only (the
 * platform stamps them, and a reader going through Android's keyboard app still presses a key per
 * character), else
 * from its input events, where characters inserted together arrive together; a composition counts
 * as one arrival however long it is. Which kind of key came (a real key, or Android's on-screen
 * "Unidentified"/229) is kept for the diagnosis only: Android keyboards send digits as real keys and
 * a reader through the keyboard app arrives as 229, so neither proves anything on its own.
 */

/** Longest gap between two characters of a reader's burst */
export const FAST_GAP_MS = 40;
/** Longest pause between two bursts of one code (a Bluetooth reader's radio) */
export const MAX_PAUSE_MS = 800;
/** Shortest code a reader is believed for */
const MIN_LENGTH = 3;

/** One change of a text field: when it came, how (InputEvent.inputType) and how long the text was after it. */
export interface InputChange {
  readonly at: number;
  readonly kind: string;
  /** characters in the field after the change; absent, one more than before */
  readonly length?: number;
}

/** The change an input event made, for judgeScan. */
export function inputChange(event: { readonly timeStamp: number; readonly nativeEvent: Event; readonly target: { readonly value: string } }): InputChange {
  const kind = (event.nativeEvent as InputEvent).inputType;
  return { at: event.timeStamp, kind: typeof kind === 'string' ? kind : '', length: event.target.value.length };
}

/**
 * A key pressed in a field, never which one (the code is not kept): hard — a real key, from a reader
 * or a physical keyboard; soft — an on-screen keyboard or input method ("Unidentified", keyCode 229);
 * repeat — a character key held down; enter; other — arrows, Backspace, Tab…
 */
export type KeyKind = 'hard' | 'soft' | 'repeat' | 'enter' | 'other';

export interface KeyPress {
  readonly at: number;
  readonly kind: KeyKind;
}

const MODIFIERS = new Set(['Shift', 'Control', 'Alt', 'AltGraph', 'Meta', 'CapsLock', 'NumLock', 'Fn']);

/** What a keydown was, for judgeScan; null for a modifier (a reader presses Shift for capitals). */
export function keyPress(event: { readonly timeStamp: number; readonly key: string; readonly keyCode?: number; readonly repeat?: boolean }): KeyPress | null {
  const { key } = event;
  if (MODIFIERS.has(key)) return null;
  const soft = key === 'Unidentified' || key === 'Process' || event.keyCode === 229;
  const kind: KeyKind =
    key === 'Enter' ? 'enter' : soft ? 'soft' : key.length !== 1 ? 'other' : event.repeat ? 'repeat' : 'hard';
  return { at: event.timeStamp, kind };
}

/** How a code got into a field since it was last empty. */
export interface ScanTrace {
  readonly changes: readonly InputChange[];
  readonly keys?: readonly KeyPress[];
  /** the Enter that ended it (a reader's suffix, or a person's key) */
  readonly enterAt?: number | null;
}

/** Why judgeScan decided as it did. */
export type ScanReason = 'whole' | 'fast' | 'short' | 'none' | 'pasted' | 'edited' | 'held' | 'composed' | 'slow' | 'pause';

/** The timing of a code, without the code: what a refusal is diagnosed by. */
export interface ScanShape {
  readonly length: number;
  /** input events */
  readonly changes: number;
  readonly hardKeys: number;
  readonly softKeys: number;
  /** where the times came from */
  readonly timedBy: 'keys' | 'input';
  /** gaps judged: between the characters, and to a fast Enter */
  readonly gaps: number;
  /** gaps of at most FAST_GAP_MS */
  readonly fast: number;
  readonly medianMs: number | null;
  readonly longestMs: number | null;
  /** first character to Enter (or to the last character) */
  readonly totalMs: number | null;
  /** last character to Enter */
  readonly enterMs: number | null;
}

export interface ScanVerdict {
  /** read by a barcode reader (else typed by hand) */
  readonly reader: boolean;
  readonly reason: ScanReason;
  readonly shape: ScanShape;
}

const PASTED = new Set(['insertFromPaste', 'insertFromPasteAsQuotation', 'insertFromDrop', 'insertReplacementText', 'insertFromYank']);
const COMPOSED = new Set(['insertCompositionText', 'insertFromComposition']);
// a composition's own bookkeeping (Safari takes it out before putting it back in)
const IGNORED = new Set(['deleteCompositionText']);
const EDITED = (kind: string) => (kind.startsWith('delete') && !IGNORED.has(kind)) || kind.startsWith('history');

const round = (ms: number) => Math.round(ms);
const median = (values: readonly number[]) => {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
};

/** Whether a code in a text field came from a barcode reader, why, and its timing (see the rule above). */
export function judgeScan(trace: ScanTrace, value: string): ScanVerdict {
  const { changes } = trace;
  const keys = trace.keys ?? [];
  const charKeys = keys.filter((k) => k.kind === 'hard' || k.kind === 'soft' || k.kind === 'repeat');

  // what went in: each change's characters, and whether the text ever shrank
  let previous = 0;
  let edited = false;
  let inserts = 0;
  let whole: InputChange | null = null;
  const fromInput: number[] = [];
  for (const change of changes) {
    if (IGNORED.has(change.kind)) continue;
    if (EDITED(change.kind)) edited = true;
    const length = change.length ?? (changes.length === 1 ? value.length : previous + 1);
    const added = length - previous;
    previous = length;
    if (added < 0) edited = true;
    if (added <= 0) continue;
    inserts++;
    whole = added >= value.length ? change : null;
    if (COMPOSED.has(change.kind)) fromInput.push(change.at);
    else for (let i = 0; i < added; i++) fromInput.push(change.at);
  }

  // a key press per character (a stray one or two aside): more would be an input method pressing extra ones
  const byKeys = value.length > 0 && charKeys.length >= value.length && charKeys.length <= value.length + 2;
  const times = byKeys ? charKeys.map((k) => k.at) : fromInput;
  const between = times.slice(1).map((t, i) => Math.max(0, t - times[i]!));
  const last = times.length > 0 ? times[times.length - 1]! : null;
  const enterAt = trace.enterAt ?? null;
  const enterGap = enterAt !== null && last !== null ? Math.max(0, enterAt - last) : null;
  // a reader's Enter comes with its last burst and counts; a later one (a pause, a person's key) costs nothing
  const gaps = enterGap !== null && enterGap <= FAST_GAP_MS ? [...between, enterGap] : between;
  const fast = gaps.filter((g) => g <= FAST_GAP_MS).length;
  const shape: ScanShape = {
    length: value.length,
    changes: changes.length,
    hardKeys: keys.filter((k) => k.kind === 'hard' || k.kind === 'repeat').length,
    softKeys: keys.filter((k) => k.kind === 'soft').length,
    timedBy: byKeys ? 'keys' : 'input',
    gaps: gaps.length,
    fast,
    medianMs: gaps.length > 0 ? round(median(gaps)) : null,
    longestMs: gaps.length > 0 ? round(Math.max(...gaps)) : null,
    totalMs: times.length > 0 ? round((enterAt ?? last!) - times[0]!) : null,
    enterMs: enterGap === null ? null : round(enterGap),
  };
  const verdict = (reader: boolean, reason: ScanReason): ScanVerdict => ({ reader, reason, shape });

  if (value.length < MIN_LENGTH) return verdict(false, 'short');
  if (changes.length === 0 && !byKeys) return verdict(false, 'none');
  if (changes.some((c) => PASTED.has(c.kind))) return verdict(false, 'pasted');
  if (keys.some((k) => k.kind === 'repeat')) return verdict(false, 'held');
  if (edited) return verdict(false, 'edited');
  // the whole code at once: a reader (or its app) that sends it as one string; a keyboard composes letter by letter
  if (!byKeys && inserts === 1 && whole !== null) return verdict(!COMPOSED.has(whole.kind), COMPOSED.has(whole.kind) ? 'composed' : 'whole');
  if (gaps.length === 0 || fast * 2 <= gaps.length) return verdict(false, 'slow');
  if (between.some((g) => g > MAX_PAUSE_MS)) return verdict(false, 'pause');
  return verdict(true, 'fast');
}

/** Whether a code in a text field came from a barcode reader (judgeScan, without the why). */
export function readByScanner(changes: readonly InputChange[], value: string): boolean {
  return judgeScan({ changes }, value).reader;
}

const REASONS: Readonly<Record<ScanReason, string>> = {
  whole: 'kod tek seferde geldi',
  fast: 'okuyucu hızında geldi',
  short: 'kod 3 karakterden kısa',
  none: 'yazılış kaydı yok',
  pasted: 'yapıştırıldı ya da öneriden seçildi',
  edited: 'silindi ya da düzeltildi',
  held: 'bir tuş basılı tutuldu',
  composed: 'ekran klavyesinden tek seferde geldi',
  slow: 'karakterlerin çoğu el hızında geldi',
  pause: 'iki karakter arasında uzun bekleme',
};

/** A verdict in one line for the person who sorts out a refused reader (never the code itself). */
export function describeScan({ reason, shape: s }: ScanVerdict): string {
  const parts = [`${s.length} karakter`, `${s.changes} değişiklik`, `tuş: ${s.hardKeys} fiziksel, ${s.softKeys} ekran klavyesi`];
  if (s.gaps > 0) {
    parts.push(`aralar (${s.timedBy === 'keys' ? 'tuştan' : 'girişten'}): ${s.fast}/${s.gaps} hızlı, ortanca ${s.medianMs} ms, en uzun ${s.longestMs} ms`);
  }
  if (s.totalMs !== null) parts.push(`toplam ${s.totalMs} ms`);
  parts.push(s.enterMs === null ? 'Enter yok' : `Enter ${s.enterMs} ms sonra`);
  return `${REASONS[reason]} · ${parts.join(' · ')}`;
}

/** A refusal leaves its timing in the browser console as well (for whoever plugs the phone in). */
export function logRefusal(verdict: ScanVerdict): void {
  console.info(`[okutma] elle yazılmış sayıldı: ${describeScan(verdict)}`, verdict.shape);
}

/**
 * Records how a code gets into a field (changes, key presses, the Enter) since the field was last
 * empty — or since a key replaced all of its text — for judgeScan.
 */
export function createScanRecorder() {
  let changes: InputChange[] = [];
  let keys: KeyPress[] = [];
  let enterAt: number | null = null;
  // keys pressed before the latest change (a key's keydown comes before its change)
  let keysBefore = 0;
  const clear = () => {
    changes = [];
    keys = [];
    enterAt = null;
    keysBefore = 0;
  };
  return {
    /** onChange: an emptied field starts again, and so does one whose text a key replaced (a scan over a selection) */
    change(event: Parameters<typeof inputChange>[0]): void {
      const change = inputChange(event);
      if (change.length === 0) return clear();
      const previous = changes.length > 0 ? changes[changes.length - 1]!.length ?? 0 : 0;
      if (change.length === 1 && previous >= 1 && !EDITED(change.kind) && !PASTED.has(change.kind)) {
        changes = [];
        keys = keys.slice(keysBefore);
        enterAt = null;
      }
      changes.push(change);
      keysBefore = keys.length;
    },
    /** onKeyDown (before the field's own handling of Enter) */
    key(event: Parameters<typeof keyPress>[0]): void {
      const press = keyPress(event);
      if (press === null) return;
      if (press.kind === 'enter') enterAt = press.at;
      else keys.push(press);
    },
    trace(): ScanTrace {
      return { changes, keys, enterAt };
    },
    clear,
  };
}

export type ScanRecorder = ReturnType<typeof createScanRecorder>;

/**
 * The part of a trace that came after the field held `before` characters (a code read after a count a
 * person typed into the same box), as if the field had been empty then: judgeScan judges the code alone.
 */
export function traceAfter(trace: ScanTrace, before: number): ScanTrace {
  const { changes } = trace;
  // after the last change that left the field at most `before` long
  let i = changes.length;
  while (i > 0 && (changes[i - 1]!.length ?? before + 1) > before) i--;
  const since = i > 0 ? changes[i - 1]!.at : -Infinity;
  return {
    changes: changes.slice(i).map((c) => (c.length === undefined ? c : { ...c, length: c.length - before })),
    // a key comes before its change: the code's keys are the ones after that change
    keys: (trace.keys ?? []).filter((k) => k.at > since),
    enterAt: trace.enterAt ?? null,
  };
}

/** A count a person types into a box (how many one scan counts at mal kabul): 1 to 10 000, else null. */
export function typedCount(text: string): number | null {
  const n = /^\d{1,5}$/.test(text.trim()) ? Number(text.trim()) : NaN;
  return n >= 1 && n <= 10_000 ? n : null;
}

/** Whether the box's changes from `start` on came at a reader's pace: the text only grew, more than half of the gaps between its characters fast (judgeScan's bursts). */
function readerPace(log: readonly { readonly value: string; readonly at: number }[], start: number): boolean {
  const gaps: number[] = [];
  let length = start > 0 ? log[start - 1]!.value.length : 0;
  for (let i = start; i < log.length; i++) {
    const added = log[i]!.value.length - length;
    length = log[i]!.value.length;
    if (added <= 0) return false;
    if (i > start) gaps.push(log[i]!.at - log[i - 1]!.at);
    // characters that came in one change came together
    for (let k = 1; k < added; k++) gaps.push(0);
  }
  return gaps.length > 0 && gaps.filter((g) => g <= FAST_GAP_MS).length * 2 > gaps.length;
}

/**
 * Where a reader's code begins in a box a person also types a count into (mal kabul). The box's
 * changes fall into bursts (at most FAST_GAP_MS apart inside one); the code is the last bursts at most
 * MAX_PAUSE_MS apart, at a reader's pace (more than half of its gaps fast), at least `minLength` long.
 * A person's count can only come before it: the lone keys that open those bursts are the person's when,
 * with what was typed before them, they make a count (typedCount); a lone key further on is one of the
 * reader's (a Bluetooth reader's radio sends one now and then). When a lone digit could be either, the
 * code's check digit decides: a number that is a GTIN with the digit and none without it keeps it.
 * `log` holds the box's text after each change and when it came, `value` its text now. Returns how
 * many characters before the code were typed, or null when no code was read (a person's typing).
 */
export function readerTail(log: readonly { readonly value: string; readonly at: number }[], value: string, minLength = 4): number | null {
  if (log.length === 0) return null;
  // bursts: the first and last change of each
  const bursts: [number, number][] = [];
  log.forEach((entry, i) => {
    if (i > 0 && entry.at - log[i - 1]!.at <= FAST_GAP_MS) bursts[bursts.length - 1]![1] = i;
    else bursts.push([i, i]);
  });
  const textBefore = (i: number) => (i > 0 ? log[i - 1]!.value : '');
  const size = ([first, last]: [number, number]) => log[last]!.value.length - textBefore(first).length;
  // the last bursts with no longer pause between them
  let run = bursts.length - 1;
  while (run > 0 && log[bursts[run]![0]]!.at - log[bursts[run - 1]![1]]!.at <= MAX_PAUSE_MS) run--;
  // where the code may begin: where they start, or after each lone key opening them
  const starts = [bursts[run]![0]];
  for (let b = run; b < bursts.length - 1 && size(bursts[b]!) === 1; b++) starts.push(bursts[b + 1]![0]);
  const cuts = starts
    .filter((start, j) => {
      const before = textBefore(start);
      if (!value.startsWith(before) || value.length - before.length < minLength) return false;
      // lone keys taken for the person's are a count with what came before them
      return (j === 0 || typedCount(before) !== null) && readerPace(log, start);
    })
    .map((start) => textBefore(start).length);
  if (cuts.length === 0) return null;
  // the lone keys are the person's, unless only a code with one of them is a GTIN
  const cut = cuts[cuts.length - 1]!;
  if (isValidGtin(value.slice(cut))) return cut;
  return [...cuts].reverse().find((c) => isValidGtin(value.slice(c))) ?? cut;
}

export interface WedgeOptions {
  /** Shortest code accepted */
  readonly minLength?: number;
  /** A pause this long starts a new code */
  readonly resetAfterMs?: number;
}

/**
 * Scans typed while no field has the focus: the keys gathered up to Enter, judged like a field's
 * (judgeScan, timed by the keys). Pure, so it can be tested.
 */
export function createWedgeDetector(onScan: (code: string) => void, options: WedgeOptions = {}) {
  const minLength = options.minLength ?? MIN_LENGTH;
  const resetAfterMs = options.resetAfterMs ?? MAX_PAUSE_MS;
  let buffer = '';
  let keys: KeyPress[] = [];
  let last = -Infinity;
  return {
    /** Feeds one key; true when it completed a scan. */
    key(key: string, at: number, repeat = false): boolean {
      if (at - last > resetAfterMs) {
        buffer = '';
        keys = [];
      }
      last = at;
      if (key === 'Enter') {
        const code = buffer;
        const pressed = keys;
        buffer = '';
        keys = [];
        if (code.length >= minLength && judgeScan({ changes: [], keys: pressed, enterAt: at }, code).reader) {
          onScan(code);
          return true;
        }
        return false;
      }
      if (key.length === 1) {
        buffer += key;
        keys.push({ at, kind: repeat ? 'repeat' : 'hard' });
      }
      return false;
    },
  };
}

const typingTarget = (target: EventTarget | null) =>
  target instanceof HTMLElement && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));

/** Scans that arrive while no input has the focus (each screen's scan field handles the rest). */
export function useWedgeScanner(onScan: ((code: string) => void) | null): void {
  const handler = useRef(onScan);
  handler.current = onScan;
  useEffect(() => {
    const detector = createWedgeDetector((code) => handler.current?.(normalizeScan(code)));
    const listen = (event: KeyboardEvent) => {
      if (typingTarget(event.target) || event.ctrlKey || event.altKey || event.metaKey) return;
      if (detector.key(event.key, event.timeStamp, event.repeat)) event.preventDefault();
    };
    window.addEventListener('keydown', listen);
    return () => window.removeEventListener('keydown', listen);
  }, []);
}
